import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useHardConfirm } from "../../components/HardConfirmDialog";
import {
  type GameCatalogRecord,
  type ProviderUnit,
  type VenueGameRecord,
  providerApi,
} from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { gameCopy, type GameCopyKey } from "./game-copy";

interface Props {
  units: ProviderUnit[];
  venueId: string;
  venueName: string;
}

function normalizeGameText(value: string) {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function sameUnitScope(left: string[], right: string[]) {
  const leftIds = new Set(left);
  const rightIds = new Set(right);
  return leftIds.size === rightIds.size && [...leftIds].every((id) => rightIds.has(id));
}

function formatReleased(value: string | null) {
  if (!value) {
    return null;
  }
  return value.slice(0, 4);
}

export function GamesCard({ units, venueId, venueName }: Props) {
  const { t, resolvedLanguage, direction } = useAppPreferences();
  const copy = (key: GameCopyKey, values?: Record<string, string | number>) => gameCopy(resolvedLanguage, key, values);
  const { confirm, dialog } = useHardConfirm();
  const [games, setGames] = useState<VenueGameRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<GameCatalogRecord[]>([]);
  const [searchSource, setSearchSource] = useState<"cache" | "rawg" | "local" | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchedQuery, setSearchedQuery] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<"short" | "unavailable" | null>(null);
  const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([]);
  const [editingGameId, setEditingGameId] = useState<string | null>(null);
  const [savingGameKey, setSavingGameKey] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualTitle, setManualTitle] = useState("");
  const [manualCategory, setManualCategory] = useState("other");
  const [manualUnitIds, setManualUnitIds] = useState<string[]>([]);
  const [manualErrors, setManualErrors] = useState({ title: false, category: false, units: false });
  const [manualSaveFailed, setManualSaveFailed] = useState(false);
  const [savedTitle, setSavedTitle] = useState<string | null>(null);
  const manualTitleRef = useRef<HTMLInputElement>(null);
  const manualCategoryRef = useRef<HTMLInputElement>(null);
  const manualUnitsRef = useRef<HTMLDivElement>(null);
  const manualTriggerRef = useRef<HTMLButtonElement>(null);
  const catalogUnitsRef = useRef<HTMLDivElement>(null);
  const saveInFlight = useRef(false);
  const venueScope = useRef(0);
  const formId = useId();

  const eligibleUnits = useMemo(() => units.filter((unit) => ["console", "pc", "room"].includes(unit.type)), [units]);
  const eligibleUnitIds = useMemo(() => new Set(eligibleUnits.map((unit) => unit.id)), [eligibleUnits]);
  const unitNameById = useMemo(() => new Map(units.map((unit) => [unit.id, unit.name])), [units]);
  const gamesByRawgId = useMemo(() => new Map(games.filter((game) => game.rawg_id !== null).map((game) => [game.rawg_id, game])), [games]);
  const editingGame = editingGameId ? games.find((game) => game.id === editingGameId) ?? null : null;
  const duplicateManualGame = games.find((game) => game.rawg_id === null
    && normalizeGameText(game.title) === normalizeGameText(manualTitle)
    && normalizeGameText(game.category) === normalizeGameText(manualCategory)
    && sameUnitScope(game.unit_ids, manualUnitIds));
  const saving = savingGameKey !== null;
  const validUnitScope = (ids: string[]) => ids.length > 0 && ids.every((id) => eligibleUnitIds.has(id));

  const loadGames = async (justCreated?: VenueGameRecord) => {
    const scope = venueScope.current;
    setLoading(true);
    setLoadFailed(false);
    try {
      const res = await providerApi.listGames(venueId);
      if (scope !== venueScope.current) return;
      // Keep the confirmed create response visible if a cached list has not caught up yet.
      setGames(justCreated && !res.games.some((game) => game.id === justCreated.id) ? [justCreated, ...res.games] : res.games);
    } catch {
      if (scope === venueScope.current) setLoadFailed(true);
    } finally {
      if (scope === venueScope.current) setLoading(false);
    }
  };

  useEffect(() => {
    venueScope.current += 1;
    saveInFlight.current = false;
    setGames([]);
    setError(null);
    setSearchQuery("");
    setSearchResults([]);
    setSearchSource(null);
    setSearchedQuery(null);
    setSearchError(null);
    setSearching(false);
    setEditingGameId(null);
    setSelectedUnitIds([]);
    setSavingGameKey(null);
    setManualOpen(false);
    setManualTitle("");
    setManualCategory("other");
    setManualUnitIds([]);
    setManualErrors({ title: false, category: false, units: false });
    setManualSaveFailed(false);
    setSavedTitle(null);
    void loadGames();
    return () => { venueScope.current += 1; };
  }, [venueId]);

  useEffect(() => {
    if (manualOpen) manualTitleRef.current?.focus();
  }, [manualOpen]);

  useEffect(() => {
    if (editingGameId) catalogUnitsRef.current?.focus();
  }, [editingGameId]);

  const startSave = (key: string) => {
    if (saveInFlight.current) return false;
    saveInFlight.current = true;
    setSavingGameKey(key);
    setError(null);
    setSavedTitle(null);
    return true;
  };

  const finishSave = (scope: number) => {
    if (scope !== venueScope.current) return;
    saveInFlight.current = false;
    setSavingGameKey(null);
  };

  const openManual = (title?: string) => {
    if (title && !manualTitle.trim()) setManualTitle(title);
    setManualOpen(true);
    setSavedTitle(null);
    manualTitleRef.current?.focus();
  };

  const closeManual = () => {
    setManualOpen(false);
    manualTriggerRef.current?.focus();
  };

  const handleCreateManual = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saveInFlight.current || loading) return;
    const title = manualTitle.trim();
    const category = manualCategory.trim().toLowerCase();
    const validation = {
      title: title.length < 1 || title.length > 255,
      category: category.length < 1 || category.length > 64,
      units: !validUnitScope(manualUnitIds),
    };
    setManualErrors(validation);
    setManualSaveFailed(false);
    if (validation.title || validation.category || validation.units) {
      if (validation.title) manualTitleRef.current?.focus();
      else if (validation.category) manualCategoryRef.current?.focus();
      else manualUnitsRef.current?.focus();
      return;
    }
    // Only exact normalized custom title/category/unit sets are duplicates.
    // Imported games, other editions and different scopes remain separate records.
    if (duplicateManualGame || !startSave("manual")) return;
    const scope = venueScope.current;
    try {
      const created = await providerApi.createGame(venueId, {
        title,
        category,
        unit_ids: [...manualUnitIds],
        is_active: true,
        // The existing create input omits rawg_id; the API defaults it to null.
      });
      if (scope !== venueScope.current) return;
      setGames((current) => [created, ...current.filter((game) => game.id !== created.id)]);
      setSavedTitle(created.title);
      setManualOpen(false);
      setManualTitle("");
      setManualCategory("other");
      setManualUnitIds([]);
      await loadGames(created);
    } catch {
      if (scope !== venueScope.current) return;
      setManualSaveFailed(true);
      // A lost response can follow a committed create. Recheck saved records
      // before enabling retry, without claiming backend uniqueness or idempotency.
      await loadGames();
    } finally {
      finishSave(scope);
    }
  };

  const toggleUnit = (unitId: string, manual = false) => {
    const setIds = manual ? setManualUnitIds : setSelectedUnitIds;
    setIds((current) => current.includes(unitId)
      ? current.filter((id) => id !== unitId)
      : [...current, unitId]);
  };

  const selectUnitsForGame = (game: VenueGameRecord) => {
    setEditingGameId(game.id);
    setSelectedUnitIds([...game.unit_ids]);
    catalogUnitsRef.current?.focus();
  };

  const handleSearch = async (event?: React.FormEvent) => {
    event?.preventDefault();
    const query = searchQuery.trim();
    if (query.length < 2) {
      setSearchError("short");
      return;
    }

    const scope = venueScope.current;
    setSearching(true);
    setSearchError(null);
    setSearchResults([]);
    setSearchSource(null);
    setSearchedQuery(null);
    try {
      const response = await providerApi.searchGameCatalog(query);
      if (scope !== venueScope.current) return;
      setSearchResults(response.results);
      setSearchSource(response.source);
      setSearchQuery(query);
      setSearchedQuery(query);
    } catch {
      if (scope === venueScope.current) setSearchError("unavailable");
    } finally {
      if (scope === venueScope.current) setSearching(false);
    }
  };

  const handleImport = async (catalogGame: GameCatalogRecord) => {
    if (!validUnitScope(selectedUnitIds)) {
      setError(copy("Choose at least one console, PC or room unit at this venue."));
      return;
    }

    const existing = gamesByRawgId.get(catalogGame.rawg_id);
    if (!startSave(String(catalogGame.rawg_id))) return;
    const scope = venueScope.current;
    try {
      if (existing) {
        const updated = await providerApi.updateGame(venueId, existing.id, { unit_ids: selectedUnitIds, is_active: true });
        if (scope !== venueScope.current) return;
        setGames((current) => current.map((game) => game.id === existing.id ? updated : game));
      } else {
        const created = await providerApi.importGame(venueId, {
          rawg_id: catalogGame.rawg_id,
          unit_ids: selectedUnitIds,
          is_active: true,
        });
        if (scope !== venueScope.current) return;
        setGames((current) => [created, ...current]);
      }
      setEditingGameId(null);
    } catch {
      if (scope === venueScope.current) setError(t("Could not import game from RAWG."));
    } finally {
      finishSave(scope);
    }
  };

  const handleSaveUnitScope = async () => {
    if (!editingGame) {
      return;
    }
    if (!validUnitScope(selectedUnitIds)) {
      setError(copy("Choose at least one console, PC or room unit at this venue."));
      return;
    }

    if (!startSave(editingGame.id)) return;
    const scope = venueScope.current;
    try {
      const updated = await providerApi.updateGame(venueId, editingGame.id, { unit_ids: selectedUnitIds });
      if (scope !== venueScope.current) return;
      setGames((current) => current.map((game) => game.id === editingGame.id ? updated : game));
      setEditingGameId(null);
      setSelectedUnitIds([]);
    } catch {
      if (scope === venueScope.current) setError(t("Could not update game."));
    } finally {
      finishSave(scope);
    }
  };

  const handleToggleActive = async (game: VenueGameRecord) => {
    if (!startSave(game.id)) return;
    const scope = venueScope.current;
    try {
      const updated = await providerApi.updateGame(venueId, game.id, { is_active: !game.is_active });
      if (scope !== venueScope.current) return;
      setGames((prev) => prev.map((g) => (g.id === game.id ? updated : g)));
    } catch {
      if (scope === venueScope.current) setError(t("Could not update game."));
    } finally {
      finishSave(scope);
    }
  };

  const handleDelete = async (gameId: string, title: string) => {
    if (saveInFlight.current) return;
    const scope = venueScope.current;
    const confirmationValue = title.trim() || "DELETE";
    const isConfirmed = await confirm({
      confirmationValue,
      confirmButtonLabel: t("Delete game"),
      confirmFieldLabel: t("Type {value} to permanently delete this game", { value: confirmationValue }),
      consequence: t("This game disappears from the venue menu immediately and the change will still sync if it was queued offline."),
      description: t("This permanently removes {name} from {venueName}.", { name: title, venueName }),
      title: t("Delete {name}?", { name: title }),
    });
    if (!isConfirmed || scope !== venueScope.current || !startSave(gameId)) {
      return;
    }

    try {
      await providerApi.deleteGame(venueId, gameId);
      if (scope !== venueScope.current) return;
      setGames((prev) => prev.filter((g) => g.id !== gameId));
      if (editingGameId === gameId) {
        setEditingGameId(null);
        setSelectedUnitIds([]);
      }
    } catch {
      if (scope === venueScope.current) setError(t("Could not delete game."));
    } finally {
      finishSave(scope);
    }
  };

  const renderUnitSelector = (manual = false) => {
    const selectedIds = manual ? manualUnitIds : selectedUnitIds;
    return (
      <div
        aria-describedby={manual && manualErrors.units ? `${formId}-units-error` : undefined}
        aria-invalid={manual && manualErrors.units || undefined}
        aria-label={copy(manual ? "Manual game units" : "Catalog and saved game units")}
        className="ops-subtle-surface grid gap-2 p-3 outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
        ref={manual ? manualUnitsRef : undefined}
        role="group"
        tabIndex={manual ? -1 : undefined}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="ops-label">{!manual && editingGame ? copy("Edit units for {title}", { title: editingGame.title }) : t("Assign to units")}</p>
          <span className="ops-chip">{t("{count} selected", { count: selectedIds.length })}</span>
        </div>
        <p className="ops-copy text-xs">{copy("Games can be assigned to console, PC and room units only.")}</p>
        {eligibleUnits.length === 0 ? (
          <p className="ops-copy text-sm">{copy("Create a console, PC or room unit before assigning games.")}</p>
        ) : (
          <div className="grid max-h-[14rem] gap-2 overflow-y-auto pe-1 sm:grid-cols-2 xl:grid-cols-3">
            {eligibleUnits.map((unit) => (
              <label className="ops-checkbox-row p-2 gap-2" key={unit.id}>
                <input checked={selectedIds.includes(unit.id)} disabled={saving} onChange={() => toggleUnit(unit.id, manual)} type="checkbox" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-white">{unit.name}</span>
                  <span className="block text-xs text-slate-400">{t(unit.type)}</span>
                </span>
              </label>
            ))}
          </div>
        )}
        {manual && manualErrors.units ? <p className="text-sm text-[var(--color-error)]" id={`${formId}-units-error`} role="alert">{copy("Choose at least one console, PC or room unit at this venue.")}</p> : null}
        {!manual && editingGame ? (
          <div className="flex flex-wrap gap-2">
            <button className="ops-button" data-size="sm" data-variant="primary" disabled={saving} onClick={() => void handleSaveUnitScope()} type="button">
              {savingGameKey === editingGame.id ? t("Saving…") : t("Save unit scope")}
            </button>
            <button className="ops-button" data-size="sm" data-variant="secondary" disabled={saving} onClick={() => { setEditingGameId(null); setSelectedUnitIds([]); }} type="button">
              {t("Cancel")}
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const renderGameChips = (game: VenueGameRecord) => {
    const assignedUnitNames = game.unit_ids.map((unitId) => unitNameById.get(unitId)).filter(Boolean) as string[];
    const visibleAssignedUnits = assignedUnitNames.slice(0, 3);
    const hiddenAssignedUnitCount = Math.max(0, assignedUnitNames.length - visibleAssignedUnits.length);
    return (
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        <span className="ops-chip" data-tone="info">{t("Unit scoped")}</span>
        {visibleAssignedUnits.length === 0 ? <span className="ops-chip" data-tone="warning">{t("No units assigned")}</span> : null}
        {visibleAssignedUnits.map((unitName) => <span className="ops-chip" key={unitName}>{unitName}</span>)}
        {hiddenAssignedUnitCount > 0 ? <span className="ops-chip">{t("+{count} more", { count: hiddenAssignedUnitCount })}</span> : null}
      </div>
    );
  };

  return (
    <div className="grid gap-4" dir={direction}>
      {dialog}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <p className="ops-kicker">{t("Game catalog")}</p>
          <span className="ops-chip" data-tone="info">{venueName}</span>
          <span className="ops-chip">{t(games.length === 1 ? "{count} game on record" : "{count} games on record", { count: games.length })}</span>
        </div>
        <button aria-controls={manualOpen ? `${formId}-manual` : undefined} aria-expanded={manualOpen} className="ops-button" data-size="sm" data-variant="primary" disabled={saving} onClick={() => openManual()} ref={manualTriggerRef} type="button">
          {copy("Add manually")}
        </button>
      </div>

      {savedTitle ? <div className="ops-banner break-words" data-tone="success" role="status">{copy("Added {title} to the venue library.", { title: savedTitle })}</div> : null}

      {loadFailed ? (
        <div className="ops-banner flex flex-wrap items-start justify-between gap-3" data-tone="warning" role="alert">
          <span className="min-w-0 flex-1">{copy("Could not load saved games. The games already shown are kept. You can still add manually, but duplicates can only be checked against the games currently loaded.")}</span>
          <button className="ops-button" data-size="sm" data-variant="secondary" disabled={loading || saving} onClick={() => void loadGames()} type="button">{copy("Retry loading games")}</button>
        </div>
      ) : null}

      {error ? (
        <div className="ops-banner flex items-start justify-between gap-3" data-tone="danger" role="alert">
          <span>{error}</span>
          <button className="ops-button" data-size="sm" data-variant="danger" onClick={() => setError(null)} type="button">
            {t("Dismiss")}
          </button>
        </div>
      ) : null}

      {manualOpen ? (
        <section aria-labelledby={`${formId}-manual-heading`} className="ops-surface grid gap-4" id={`${formId}-manual`}>
          <div>
            <h3 className="ops-kicker" id={`${formId}-manual-heading`}>{copy("Add a game manually")}</h3>
            <p className="ops-copy mt-1 text-sm">{copy("No catalog match needed. Enter the game's title and category, then choose where it is available.")}</p>
          </div>
          <form className="grid gap-3" noValidate onSubmit={(event) => void handleCreateManual(event)}>
            <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
              <label className="ops-field">
                <span className="ops-label" id={`${formId}-title-label`}>{copy("Game title")}</span>
                <input
                  aria-describedby={manualErrors.title ? `${formId}-title-error` : undefined}
                  aria-invalid={manualErrors.title || undefined}
                  aria-labelledby={`${formId}-title-label`}
                  className="ops-input"
                  disabled={saving}
                  maxLength={255}
                  onChange={(event) => setManualTitle(event.target.value)}
                  ref={manualTitleRef}
                  required
                  value={manualTitle}
                />
                {manualErrors.title ? <span className="text-sm text-[var(--color-error)]" id={`${formId}-title-error`} role="alert">{copy("Enter a game title (1–255 characters).")}</span> : null}
              </label>
              <label className="ops-field">
                <span className="ops-label" id={`${formId}-category-label`}>{copy("Category")}</span>
                <input
                  aria-describedby={`${formId}-category-hint${manualErrors.category ? ` ${formId}-category-error` : ""}`}
                  aria-invalid={manualErrors.category || undefined}
                  aria-labelledby={`${formId}-category-label`}
                  className="ops-input"
                  disabled={saving}
                  maxLength={64}
                  onChange={(event) => setManualCategory(event.target.value)}
                  ref={manualCategoryRef}
                  required
                  value={manualCategory}
                />
                <span className="ops-copy text-xs" id={`${formId}-category-hint`}>{copy("For example: sports, racing or other.")}</span>
                {manualErrors.category ? <span className="text-sm text-[var(--color-error)]" id={`${formId}-category-error`} role="alert">{copy("Enter a category (1–64 characters).")}</span> : null}
              </label>
            </div>
            {renderUnitSelector(true)}
            {duplicateManualGame ? (
              <div className="ops-banner flex flex-wrap items-center justify-between gap-3" data-tone="warning" role="status">
                <span className="min-w-0 flex-1">{copy("This game is already saved with the same category and units. Edit the existing game instead.")}</span>
                <button className="ops-button" data-size="sm" data-variant="secondary" disabled={saving} onClick={() => { selectUnitsForGame(duplicateManualGame); setManualOpen(false); }} type="button">{copy("Edit existing game")}</button>
              </div>
            ) : null}
            {manualSaveFailed ? <div className="ops-banner" data-tone="danger" role="alert">{copy("Could not confirm the save. Your details are kept; check the saved games before trying again.")}</div> : null}
            <div className="flex flex-wrap gap-2">
              <button className="ops-button" data-size="sm" data-variant="primary" disabled={saving || loading || Boolean(duplicateManualGame)} type="submit">
                {savingGameKey === "manual" ? t("Saving…") : duplicateManualGame ? copy("Already saved") : copy("Save game")}
              </button>
              <button className="ops-button" data-size="sm" data-variant="secondary" disabled={saving} onClick={closeManual} type="button">{t("Cancel")}</button>
            </div>
          </form>
        </section>
      ) : null}

      <div className="grid gap-3 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
        <section className="ops-surface grid content-start gap-3">
          <p className="ops-kicker">{t("RAWG search")}</p>

          <form className="flex flex-col gap-2 sm:flex-row" onSubmit={(event) => void handleSearch(event)}>
            <input
              aria-describedby={searchError === "short" ? `${formId}-search-error` : undefined}
              aria-invalid={searchError === "short" || undefined}
              aria-label={copy("Search game title")}
              className="ops-input flex-1"
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t("Search game title...")}
              value={searchQuery}
            />
            <button className="ops-button" data-size="sm" data-variant="primary" disabled={searching} type="submit">
              {searching ? t("Searching RAWG...") : t("Search RAWG")}
            </button>
          </form>

          {searchError === "short" ? <p className="text-sm text-[var(--color-error)]" id={`${formId}-search-error`} role="alert">{copy("Enter at least two characters of a game title to search.")}</p> : null}
          <p className="ops-copy text-xs">{copy("Search returns up to 12 results. If your game is missing, try its full title or add it manually.")}</p>
          {searchSource ? <span className="ops-chip w-fit">{searchSource === "rawg" ? t("Fresh RAWG results") : searchSource === "cache" ? t("Cached results") : t("Local catalog results")}</span> : null}

          <div className="outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]" ref={catalogUnitsRef} tabIndex={-1}>{renderUnitSelector()}</div>
        </section>

        <section className="ops-surface grid content-start gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="ops-kicker">{t("Search results")}</p>
            {searchResults.length > 0 ? <span className="ops-chip">{t("{count} results", { count: searchResults.length })}</span> : null}
          </div>
          {searchError === "unavailable" ? (
            <div className="ops-banner grid gap-3" data-tone="warning" role="alert">
              <p>{copy("Catalog search is unavailable right now. You can still add a game manually; saved games are unchanged.")}</p>
              <button className="ops-button w-fit" data-size="sm" data-variant="secondary" disabled={saving} onClick={() => openManual(searchQuery.trim())} type="button">{copy("Add this game manually")}</button>
            </div>
          ) : searching ? (
            <div className="ops-empty py-4 text-sm" role="status">{t("Searching RAWG...")}</div>
          ) : searchResults.length === 0 ? (
            <div className="ops-empty grid gap-3 py-4 text-sm" role="status">
              <p className="break-words">{searchedQuery !== null ? copy("No catalog results for “{query}”. Try a more specific title or add the game manually.", { query: searchedQuery }) : copy("Search by a game title for artwork and details, or add a game manually.")}</p>
              {searchedQuery !== null ? <button className="ops-button mx-auto w-fit" data-size="sm" data-variant="secondary" disabled={saving} onClick={() => openManual(searchedQuery)} type="button">{copy("Add this game manually")}</button> : null}
            </div>
          ) : (
            <div className="grid gap-2">
              {searchResults.map((result) => {
                const existing = gamesByRawgId.get(result.rawg_id);
                return (
                  <div className="ops-record-dense" key={result.rawg_id}>
                    <div className="flex flex-wrap items-center gap-3 sm:flex-nowrap">
                      {result.image_url ? (
                        <img alt={result.title} className="h-16 w-12 shrink-0 rounded-[8px] object-cover" src={result.image_url} />
                      ) : (
                        <div className="h-16 w-12 shrink-0 rounded-[8px] bg-white/[0.03]" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="break-words text-sm font-semibold text-white">{result.title}</p>
                          <span className="ops-chip">{t(result.category)}</span>
                          {formatReleased(result.released) ? <span className="ops-chip">{formatReleased(result.released)}</span> : null}
                          {result.rating ? <span className="ops-chip" data-tone="info">{result.rating.toFixed(1)}</span> : null}
                          {existing ? <span className="ops-chip" data-tone="success">{t("Already in venue")}</span> : null}
                        </div>
                        {result.platform ? <p className="mt-0.5 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{result.platform}</p> : null}
                        {result.description ? <p className="mt-0.5 line-clamp-1 text-xs text-slate-400">{result.description}</p> : null}
                      </div>
                      <button className="ops-button self-center" data-size="sm" data-variant="primary" disabled={saving} onClick={() => void handleImport(result)} type="button">
                        {savingGameKey === String(result.rawg_id) ? t("Saving…") : existing ? t("Update units") : t("Add to units")}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <section className="grid gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="ops-kicker">{t("Venue game library")}</p>
          {loading ? <span className="ops-chip">{t("Loading games…")}</span> : null}
        </div>
        {!loading && !loadFailed && games.length === 0 ? <div className="ops-empty py-4">{copy("No games yet. Search the catalog or add a game manually, then assign it to units.")}</div> : null}
        {games.map((game) => (
          <div className="ops-record-dense" key={game.id}>
            <div className="flex flex-wrap items-start gap-3 sm:flex-nowrap">
              {game.image_url ? (
                <img alt={game.title} className="h-14 w-12 shrink-0 rounded-[8px] object-cover" src={game.image_url} />
              ) : (
                <div className="h-14 w-12 shrink-0 rounded-[8px] bg-white/[0.03]" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="break-words text-sm font-semibold text-white">{game.title}</p>
                  <span className="ops-chip">{t(game.category)}</span>
                  {game.rawg_id === null ? <span className="ops-chip">{copy("Manually added")}</span> : null}
                  {game.platform ? <span className="ops-chip" data-tone="info">{game.platform}</span> : null}
                  {!game.is_active ? <span className="ops-chip" data-tone="warning">{t("Hidden")}</span> : null}
                </div>
                {game.description ? <p className="mt-0.5 line-clamp-1 text-xs text-slate-400">{game.description}</p> : null}
                {renderGameChips(game)}
              </div>
              <div className="flex flex-wrap gap-2">
                <button className="ops-button" data-size="sm" data-variant="secondary" disabled={saving} onClick={() => selectUnitsForGame(game)} type="button">
                  {t("Edit units")}
                </button>
                <button className="ops-button" data-size="sm" data-variant="secondary" disabled={saving} onClick={() => void handleToggleActive(game)} type="button">
                  {game.is_active ? t("Hide") : t("Show")}
                </button>
                <button className="ops-button" data-size="sm" data-variant="danger" disabled={saving} onClick={() => void handleDelete(game.id, game.title)} type="button">
                  {t("Delete")}
                </button>
              </div>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
